import { Profile } from "../../profileService/model/profile/Profile";
import { ClaimsAccess } from "../../profileService/model/profile/ClaimsAccess";
import { VarianceRequest as Variance } from "../model/varianceRequest/VarianceRequest";

/**
 * Utility class for checking user permissions in the application.
 * Methods that gate elevated access require a currentCenter parameter
 * to ensure permissions are scoped to the user's assigned facilities.
 */
export class PermissionChecker {

  /**
   * Checks if the user has center access for the given center code
   */
  private static hasCenterAccess(profile: Profile, centerCode: string | undefined): boolean {
    if (!centerCode) return false;
    return profile.centerAccess?.some(c => c.code === centerCode) ?? false;
  }

  /**
   * Checks if the user can edit a variance request
   * @param profile User profile with role and access information
   * @param variance The variance request to check
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user can edit the variance
   */
  static canEditVariance(profile: Profile | undefined, variance: Variance | undefined, currentCenter?: string): boolean {
    if (!profile || !variance) return false;
    
    const centerCode = currentCenter ?? variance.center;
    const hasCenter = this.hasCenterAccess(profile, centerCode);
    
    const isAdmin = profile.role === 'Admin' && hasCenter;
    const isQualityTeam = profile.role === 'Quality Team' && hasCenter;
    
    const hasIncidentAdminClaim = hasCenter && profile.claimsAccess?.find((p: ClaimsAccess) => 
      p.code === 'IncidentAdmin') !== undefined;
    
    const hasAdminClaims = hasCenter && profile.claimsAccess?.find((p: ClaimsAccess) => 
      p.code === 'IncidentAdmin' || p.code === 'IncidentQualityTeam') !== undefined;
    
    const isCreator = variance.createdBy === profile.upn;

    const isAssigned = variance.assignedUsers?.some((p: any) => p.upn === profile.upn) ?? false;
    
    // IncidentAdmin can edit for ALL statuses — but only if they have center access
    if (hasIncidentAdminClaim) {
      return true;
    }
    
    // For Complete or Cancelled status, only allow if user is Admin or has admin claims at this center
    if (variance.status === 'Complete' || variance.status === 'Cancelled') {
      return isAdmin || isQualityTeam || hasAdminClaims;
    }
    
    // For other statuses — creator and assigned users can always edit their own
    return isAdmin || isQualityTeam || hasAdminClaims || isCreator || isAssigned;
  }

  /**
   * Checks if user can view the director tab in variance
   * @param profile User profile with role and access information
   * @param variance The variance request to check
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user can view director tab
   */
  static isDirectorTabEnabled(profile: Profile | undefined, variance: Variance | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    const centerCode = currentCenter ?? variance?.center;
    const hasCenter = this.hasCenterAccess(profile, centerCode);
    if (hasCenter && profile.role === "Admin") return true;
    if (hasCenter && profile.role === "Quality Team") return true;
    if (hasCenter && profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined) return true;
    if (hasCenter && profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentQualityTeam") !== undefined) return true;
    if (variance?.assignedUsers && variance?.assignedUsers.find((p: any) => p.upn === profile?.upn)) return true;
    return false;
  }

  /**
   * Checks if user is an admin for variance-related operations at the given center
   * @param profile User profile with role and access information
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user has admin privileges for variances at this center
   */
  static isVarianceAdmin(profile: Profile | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    if (!currentCenter) {
      return profile.role === "Admin" || 
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
    }
    const hasCenter = this.hasCenterAccess(profile, currentCenter);
    if (!hasCenter) return false;
    return profile.role === "Admin" || 
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
  }

  /**
   * Checks if user is on the quality team for variance-related operations at the given center
   * @param profile User profile with role and access information
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user has quality team privileges for variances at this center
   */
  static isVarianceQualityTeam(profile: Profile | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    if (!currentCenter) {
      return profile.role === "Quality Team" || 
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentQualityTeam") !== undefined;
    }
    const hasCenter = this.hasCenterAccess(profile, currentCenter);
    if (!hasCenter) return false;
    return profile.role === "Quality Team" || 
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentQualityTeam") !== undefined;
  }

  /**
   * Checks if user is an approver for variance requests at the given center
   * @param profile User profile with role and access information
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user is an approver
   */
  static isVarianceApprover(profile: Profile | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    if (!currentCenter) {
      return profile.role === "Admin" || 
             profile.role === "Quality Team" || 
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined ||
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentQualityTeam") !== undefined;
    }
    const hasCenter = this.hasCenterAccess(profile, currentCenter);
    if (!hasCenter) return false;
    return profile.role === "Admin" || 
           profile.role === "Quality Team" || 
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined ||
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentQualityTeam") !== undefined;
  }

  /**
   * Checks if user can close variance requests at the given center
   * @param profile User profile with role and access information
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user can close variance requests
   */
  static isVarianceCloser(profile: Profile | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    if (!currentCenter) {
      return profile.role === "Admin" || 
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
    }
    const hasCenter = this.hasCenterAccess(profile, currentCenter);
    if (!hasCenter) return false;
    return profile.role === "Admin" || 
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
  }

  /**
   * Checks if user is assigned to a variance or on quality team
   * @param profile User profile with role and access information
   * @param variance The variance request to check
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user is assigned or on quality team
   */
  static isAssignedOrQualityTeam(profile: Profile | undefined, variance: Variance | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    const centerCode = currentCenter ?? variance?.center;
    const hasCenter = this.hasCenterAccess(profile, centerCode);
    return (hasCenter && profile.role === "Quality Team") || 
           (hasCenter && profile.role === "Admin") ||
           (hasCenter && !!profile.claimsAccess?.some((p: ClaimsAccess) => p.code === "IncidentAdmin")) ||
           (hasCenter && !!profile.claimsAccess?.some((p: ClaimsAccess) => p.code === "IncidentQualityTeam")) ||
           (!!variance?.assignedUsers?.some((p: any) => p.upn === profile?.upn) && variance?.status === "Assigned");
  }

  /**
   * Checks if user is assigned to a variance
   * @param profile User profile with role and access information
   * @param variance The variance request to check
   * @returns boolean indicating if user is assigned
   */
  static isAssignedToVariance(profile: Profile | undefined, variance: Variance | undefined): boolean {
    if (!profile || !variance) return false;
    return variance?.assignedUsers?.some((p: any) => p.upn === profile?.upn);
  }

  /**
   * Checks if user can save variance data at the given center
   * @param profile User profile with role and access information
   * @param currentCenter The currently selected center code
   * @returns boolean indicating if user can save variance data
   */
  static canSaveVariance(profile: Profile | undefined, currentCenter?: string): boolean {
    if (!profile) return false;
    if (!currentCenter) {
      return profile.role === "Admin" || 
             profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
    }
    const hasCenter = this.hasCenterAccess(profile, currentCenter);
    if (!hasCenter) return false;
    return profile.role === "Admin" || 
           profile.claimsAccess?.find((p: ClaimsAccess) => p.code === "IncidentAdmin") !== undefined;
  }
}
